import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { api, API } from "@/lib/api";
import { toast } from "sonner";
import { Clapperboard, Download, Film, Loader2, RefreshCw } from "lucide-react";

const VOICES = [
  ["onyx", "Onyx · deep"], ["nova", "Nova · upbeat"], ["shimmer", "Shimmer · bright"],
  ["echo", "Echo · calm"], ["fable", "Fable · storyteller"], ["sage", "Sage · measured"],
];

export default function VideoCreator({ pkg }) {
  const [status, setStatus] = useState(pkg.video_status || (pkg.video_ready ? "ready" : "none"));
  const [voice, setVoice] = useState("onyx");
  const [captions, setCaptions] = useState(true);
  const [music, setMusic] = useState(true);
  const [elapsed, setElapsed] = useState(0);
  const [bust, setBust] = useState(Date.now());
  const pollRef = useRef(null);
  const timerRef = useRef(null);
  const pollCount = useRef(0);
  const nav = useNavigate();

  const videoUrl = `${API}/content/${pkg.id}/video?t=${bust}`;

  const poll = () => {
    pollCount.current = 0;
    pollRef.current = setInterval(async () => {
      pollCount.current += 1;
      if (pollCount.current > 96) { // ~8 min safety timeout
        clearInterval(pollRef.current); clearInterval(timerRef.current);
        setStatus("failed");
        toast.error("Video is taking too long — please try again");
        return;
      }
      try {
        const r = await api.get(`/content/${pkg.id}`);
        const st = r.data.video_status;
        if (st === "ready" || st === "failed") {
          clearInterval(pollRef.current); clearInterval(timerRef.current);
          setStatus(st); setBust(Date.now());
          if (st === "ready") toast.success("Your video is ready!");
          else toast.error("Video creation failed — try again");
        }
      } catch (e) { console.error("video status poll error (retrying):", e); }
    }, 5000);
  };

  useEffect(() => {
    if (status === "generating") {
      timerRef.current = setInterval(() => setElapsed((e) => e + 1), 1000);
      poll();
    }
    return () => { clearInterval(pollRef.current); clearInterval(timerRef.current); };
  }, []); // eslint-disable-line

  const start = async () => {
    setStatus("generating"); setElapsed(0);
    try {
      await api.post("/content/create-video", { package_id: pkg.id, voice, captions, music });
      timerRef.current = setInterval(() => setElapsed((e) => e + 1), 1000);
      poll();
    } catch (e) {
      setStatus("none");
      if (e.response?.status === 402) {
        toast.error("Out of video credits — redirecting to plans");
        setTimeout(() => nav("/pricing"), 900);
      } else {
        toast.error(e.response?.data?.detail || "Could not start");
      }
    }
  };

  return (
    <div className="bg-surface border border-white/10 rounded-md p-5" data-testid="video-creator">
      <div className="flex items-center justify-between mb-4">
        <div className="text-[11px] uppercase tracking-[0.2em] text-white/40 font-mono flex items-center gap-2">
          <Clapperboard className="w-3.5 h-3.5" /> AI Video Maker
        </div>
        {status === "ready" && (
          <a href={videoUrl} download data-testid="download-video-btn"
            className="text-xs font-mono text-signal-green hover:text-white flex items-center gap-1 transition-colors">
            <Download className="w-3.5 h-3.5" /> download
          </a>
        )}
      </div>

      {status === "ready" ? (
        <div className="space-y-3">
          <video key={bust} data-testid="generated-video" src={videoUrl} controls className="w-full rounded border border-white/10 aspect-video bg-black" />
          <div className="flex items-center gap-2">
            <select value={voice} onChange={(e) => setVoice(e.target.value)} data-testid="video-voice-select"
              className="bg-[#0A0A0A] border border-white/10 rounded px-2 py-1.5 text-xs outline-none focus:border-brand">
              {VOICES.map(([v, l]) => <option key={v} value={v} className="bg-[#121212]">{l}</option>)}
            </select>
            <button onClick={start} data-testid="recreate-video-btn"
              className="text-xs font-mono text-white/50 hover:text-white flex items-center gap-1 transition-colors">
              <RefreshCw className="w-3 h-3" /> re-generate
            </button>
          </div>
          <p className="text-[10px] text-white/30 font-mono">Ready to upload — head to Library to publish it to YouTube.</p>
        </div>
      ) : status === "generating" ? (
        <div className="aspect-video rounded border border-dashed border-white/10 flex flex-col items-center justify-center gap-3 text-center px-6">
          <Loader2 className="w-7 h-7 text-brand animate-spin" />
          <div className="font-heading text-lg">Creating your video…</div>
          <p className="text-xs text-white/40 font-mono">Voiceover + AI scenes + captions + stitching · ~2-3 min · {elapsed}s</p>
        </div>
      ) : (
        <div className="text-center py-6">
          <Film className="w-8 h-8 text-white/20 mx-auto mb-3" strokeWidth={1.25} />
          <p className="text-sm text-white/50 mb-1">Turn this package into a real narrated video.</p>
          <p className="text-xs text-white/30 mb-4 max-w-sm mx-auto">AI writes a storyboard, voices it, generates cinematic scenes, and stitches a 1080p MP4 — a ready-to-upload faceless video.</p>
          <div className="flex items-center justify-center gap-4 mb-4">
            <label className="flex items-center gap-2 text-xs text-white/60 cursor-pointer" data-testid="captions-toggle-label">
              <input type="checkbox" data-testid="captions-toggle" checked={captions} onChange={(e) => setCaptions(e.target.checked)}
                className="accent-[#FF3B30] w-4 h-4" /> Burned-in captions
            </label>
            <label className="flex items-center gap-2 text-xs text-white/60 cursor-pointer" data-testid="music-toggle-label">
              <input type="checkbox" data-testid="music-toggle" checked={music} onChange={(e) => setMusic(e.target.checked)}
                className="accent-[#FF3B30] w-4 h-4" /> Ambient music
            </label>
          </div>
          <div className="flex items-center justify-center gap-2">
            <select value={voice} onChange={(e) => setVoice(e.target.value)} data-testid="video-voice-select"
              className="bg-[#0A0A0A] border border-white/10 rounded-md px-3 py-2 text-sm outline-none focus:border-brand">
              {VOICES.map(([v, l]) => <option key={v} value={v} className="bg-[#121212]">{l}</option>)}
            </select>
            <button onClick={start} data-testid="create-video-btn"
              className="bg-brand hover:bg-brand-hover text-white rounded-md px-5 py-2 text-sm font-medium transition-colors flex items-center gap-2">
              <Clapperboard className="w-4 h-4" /> Create Video
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
